import ExtensionSidebarContactList from "@common/components/scheduled-contact-list/extension-sidebar-contact-list";
import ExtensionSidebarSearchBox from "@common/components/scheduled-contact-list/extension-sidebar-search-box";
import { namespace } from "@common-config/constants";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "clear-schedule-panel-minimized";
const WIDTH_EXPANDED = 340;
const WIDTH_MINIMIZED = 52;

/**
 * The scheduled-messages list for the chromeless ("clear") calendar.
 *
 * The clear flow hides BoardsPanel, and the search box + list live inside it —
 * so the flow had the calendar but no way to search or scan what is scheduled.
 * This renders the SAME two components BoardsPanel does
 * (ExtensionSidebarSearchBox + ExtensionSidebarContactList), so the list keeps
 * its filters, virtualisation and edit behaviour; only the shell around them is
 * ours.
 *
 * Its own minimize state, under its own storage key: the real sidebar's
 * `sidebar-minimized` belongs to a different panel and the two must not move
 * together.
 *
 * Desktop only — the mobile planner (MobileAgenda) already IS the list, so a
 * second copy beside it would be redundant on a phone.
 */
export default function ClearSchedulePanel() {
  const { t } = useTranslation(namespace);
  const direction = useAppSelector((state) => state.UserReducer.direction);
  const isRtl = direction === "rtl";

  const [minimized, setMinimized] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setMinimized((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Private mode / blocked storage: the panel still toggles for this session.
      }
    return next;
    });
  }, []);

  // The chevron points the way the panel will move, which flips with the writing
  // direction: collapsing goes toward the inline start.
  const CollapseIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon;
  const ExpandIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;
  const ToggleIcon = minimized ? ExpandIcon : CollapseIcon;
  const toggleLabel = minimized
    ? t("clearSchedulePanel.expand", "Show scheduled messages")
    : t("clearSchedulePanel.collapse", "Hide scheduled messages");

  return (
    <Box
      component="aside"
      data-testid="clear-schedule-panel"
      sx={{
        width: minimized ? WIDTH_MINIMIZED : WIDTH_EXPANDED,
        flex: `0 0 ${minimized ? WIDTH_MINIMIZED : WIDTH_EXPANDED}px`,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "var(--color-surface, #fff)",
        borderInlineEnd: "1px solid var(--clear-panel-border, #E9E7FB)",
        transition: "width 0.25s ease, flex-basis 0.25s ease",
      }}
    >
      {/* The toggle sits on its own slim row so the search box can span the full
          panel width — sharing a row truncated its placeholder. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: minimized ? "center" : "flex-end",
          paddingInline: minimized ? 0 : 0.5,
          paddingBlock: 0.25,
          flexShrink: 0,
        }}
      >
        <Tooltip title={toggleLabel} placement={minimized ? "right" : "bottom"}>
          <IconButton
            onClick={toggle}
            aria-label={toggleLabel}
            aria-expanded={!minimized}
            data-testid="clear-schedule-panel-toggle"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              color: "var(--cta-button-bg, #6366F1)",
              "&:hover": { backgroundColor: "var(--color-surface-accent, #EEF2FF)" },
            }}
          >
            <ToggleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {!minimized && (
        <Box sx={{ flexShrink: 0 }}>
          <ExtensionSidebarSearchBox />
        </Box>
      )}

      {minimized ? (
        // A quiet marker of what the rail hides, so the collapsed strip still
        // reads as "the scheduled messages" rather than an anonymous edge.
        <Tooltip title={toggleLabel} placement="right">
          <Box
            onClick={toggle}
            sx={{
              display: "flex",
              justifyContent: "center",
              paddingBlock: 1,
              cursor: "pointer",
              color: "var(--color-text-muted, #6E6A97)",
            }}
          >
            <EventNoteOutlinedIcon fontSize="small" />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <ExtensionSidebarContactList />
        </Box>
      )}
    </Box>
  );
}
